import React, { useEffect, useState } from 'react';
import API from '../../api';

// redux
import { mapDispatchProps, mapStateToProps } from '../../store/store';
import { connect } from 'react-redux';

import { PlusCircle } from 'react-feather';

import TextInputField from '../../components/textInput';
import Button from '../../components/button';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@languages/index';
import MatchRatingsEditor, { MATCH_RATING_EDITOR_MODES } from '../../components/matchRatingsEditor';
import BRAND from 'constants/brand';
import { useQueryClient } from '@tanstack/react-query';
import { queries } from 'api/api.constants';

const useForceUpdate = () => {
  const [value, setValue] = useState(0);
  return () => setValue((value) => value + 1);
};

const BrandIndex = (props: any) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.settings };
  const forceUpdate = useForceUpdate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const brandLogoInput = React.useRef(null);
  const [removeTag, setRemoveTag] = useState<boolean>();
  const [hashTagInputs, setHashTagInputs] = useState([]);
  const [hasError, setHasError] = useState<string>();

  const [matchRatingsEditorEnabled, setMatchRatingsEditorEnabled] = useState(false);
  const [isMatchRatingsEditorLoaded, setIsMatchRatingsEditorLoaded] = useState(false);

  const stylesRef = React.useRef<any[]>([]);
  const categoriesRef = React.useRef<any[]>([]);

  useEffect(() => {
    let isInputCompleted = undefined;

    if (brand) {
      if (!brand.atTag || !brand.atTag.length) {
        isInputCompleted = languageTexts.brand_text_1;
      }

      // 태그 제거하기
      if (removeTag) {
      }

      if (isInputCompleted !== hasError) {
        setHasError(isInputCompleted);
      }
    }
  });

  const fetchBrand = () => {
    API.Brand.show({ brandToken: props.auth.token })
      .then((response) => {
        setBrand(response.data.brand);
        props.setBrand(response.data.brand);

        //--- MatchRatingsEditor
        const isBeautyCategoryBrand = response.data.brand?.isCategoryBeauty;
        if (!isBeautyCategoryBrand) {
          setMatchRatingsEditorEnabled(true);
        }

        //--- HashTag
        let initialHashTags = response.data.brand.hashTags
          ? response.data.brand.hashTags.split('#').filter((tag) => tag.length)
          : [];
        if (!initialHashTags.length) {
          initialHashTags = ['', '', ''];
        }
        setHashTagInputs(initialHashTags);
      })
      .catch((error) => {
        console.log(error);
        props.showModal({
          text: languageTexts.alarm_text_1,
        });
      });
  };

  const submit = async () => {
    try {
      let params = {};

      //--- store or update MatchRatings
      if (matchRatingsEditorEnabled) {
        await API.MatchRating.bulkStoreOrUpdateRatings({
          brandToken: props.auth.token,
          styles: stylesRef.current,
          categories: categoriesRef.current,
        });
        params['hasExperiencedMatchRatingCreate'] = true;
      }

      //--- imageUrl
      params['imageUrl'] = brandLogoUrl ? brandLogoUrl : brand?.imageUrl;
      if (params['imageUrl'] == null) {
        props.showModal({ text: languageTexts.brand_text_12 });
        return false;
      }

      //--- atTag
      params['atTag'] = brand.atTag ? brand.atTag : '';
      params['atTag'] = !params['atTag'].startsWith('@') ? '@' + params['atTag'] : params['atTag'];

      //--- hashTags
      let copy = [...hashTagInputs];
      copy = copy.filter((input) => input.length);
      copy.forEach((input, index) => {
        copy[index] = input.trim();
        copy[index] = !input.startsWith('#') ? '#' + copy[index] : copy[index];
      });
      let result = '';
      copy.forEach((input) => {
        result = result.length ? result + ` ${input}` : result + input;
      });
      params['hashTags'] = result;

      //--- update Brand
      const response = await API.Brand.update({
        brandToken: props.auth.token,
        ...params,
      });

      let arr = { text: response.data.message };
      if (response.data.title.length) {
        arr['title'] = response.data.title;
      }
      props.showModal(arr);
    } catch (e) {
      props.showModal({
        text: languageTexts.brand_text_11,
      });
    }
    await fetchBrand();
    queryClient.invalidateQueries({
      queryKey: queries.brand.getBrand._def,
    });
    props.onUpdate ? props.onUpdate() : null;
  };

  const [brand, setBrand] = useState<any>(() => {
    fetchBrand();
  });
  const [brandLogoUrl, setBrandLogoUrl] = useState();

  const handleLogoImage = (e) => {
    const image = e.target.files[0];

    API.Brand.uploadLogo({
      brandToken: props.auth.token,
      image: image,
    })
      .then((response: any) => {
        const imageUrl = response.data.url;
        setBrandLogoUrl(imageUrl);
        handleChange('imageUrl', imageUrl);
      })
      .catch((error: any) => {
        props.showModal(error.response.data.message);
      })
      .finally(() => {
        forceUpdate();
      });
  };

  const handleChange = async (target, value) => {
    const formCopy = { ...brand };
    formCopy[`${target}`] = value;
    await setBrand(formCopy);
  };

  const getBrandLogoUrl = () => {
    return brandLogoUrl ? brandLogoUrl : brand?.imageUrl;
  };

  const renderPageTitle = () => {
    return (
      <div className="text-2xl font-bold tracking-tighter flex justify-center mt-12">{languageTexts.brand_text_2}</div>
    );
  };

  const renderBrandLogo = () => {
    return (
      <div
        className="flex flex-col justify-center items-center mt-12"
        onClick={() => {
          brandLogoInput.current.click();
        }}
      >
        <div
          className="relative rounded-full border-1 border-black flex justify-center items-center overflow-hidden"
          style={{
            width: '96px',
            height: '96px',
            border: '1px solid #E8E8E8',
          }}
        >
          <input type="file" ref={brandLogoInput} onChange={(e) => handleLogoImage(e)} style={{ display: 'none' }} />

          {getBrandLogoUrl() == null ? (
            <PlusCircle />
          ) : (
            <img
              className="w-full h-full object-cover"
              src={`http://nmodelin20.s3.ap-northeast-2.amazonaws.com/brand/${getBrandLogoUrl()}`}
            />
          )}
        </div>

        <div className="mt-3.5">
          <span
            style={{
              letterSpacing: '-0.05em',
            }}
          >
            {languageTexts.brand_text_3}
          </span>
        </div>
      </div>
    );
  };

  //   const renderAddTagButton = () => {
  //     return (
  //       <>
  //         <Button
  //           className="border-1 border-gray-200 hover:border-black text-center py-2"
  //           justify={"center"}
  //           text={"+"}
  //           onClick={() => {
  //             setHashTagInputs([...hashTagInputs, ""]);
  //           }}
  //         />
  //       </>
  //     );
  //   };

  const renderForm = () => {
    return (
      <div className="flex flex-col my-10">
        <div className="at-tag-form">
          <div className="text-lg font-bold tracking-tighter">{languageTexts.brand_text_4}</div>
          <div className="text-base tracking-tighter text-[#999] mt-2">
            {languageTexts.brand_text_5(props.brand.categoryId)}
          </div>
          <div className="form-input mt-4 mb-7">
            <TextInputField
              bordered
              value={brand?.atTag ?? ''}
              placeholder={languageTexts.brand_text_6}
              onChangeText={(value) => {
                handleChange(`atTag`, value);
              }}
            />
          </div>
        </div>

        <div className="hash-tag-form">
          <div className="text-lg font-bold tracking-tighter">{languageTexts.brand_text_7}</div>
          <div className="text-base tracking-tighter text-[#999] mt-2">{languageTexts._brand_text_8()}</div>
          <div className="form-input mt-4 gap-2.5 flex flex-col">
            <TextInputField
              bordered
              value={hashTagInputs[0] ?? ''}
              onChangeText={(value) => {
                let copy = [...hashTagInputs];

                copy[0] = value;

                setHashTagInputs(copy);
              }}
              placeholder={languageTexts.brand_text_9}
            />

            <TextInputField
              bordered
              value={hashTagInputs[1] ?? ''}
              onChangeText={(value) => {
                let copy = [...hashTagInputs];

                copy[1] = value;

                setHashTagInputs(copy);
              }}
              placeholder={languageTexts.brand_text_9}
            />
          </div>
        </div>

        {matchRatingsEditorEnabled && (
          <MatchRatingsEditor
            {...props}
            mode={MATCH_RATING_EDITOR_MODES.BRAND_INDEX}
            containerClassName="mt-[50px]"
            onChangeStyles={(newStyles) => {
              stylesRef.current = newStyles;
            }}
            onChangeCategories={(newCategories) => {
              categoriesRef.current = newCategories;
            }}
            onLoaded={(isLoaded) => {
              setIsMatchRatingsEditorLoaded(isLoaded);
            }}
          />
        )}

        <div className="form-submit-button mt-[50px]">
          <Button
            textColor={'WHITE'}
            buttonColor={
              (!matchRatingsEditorEnabled || isMatchRatingsEditorLoaded) && !hasError && getBrandLogoUrl() != null
                ? 'BLACK'
                : '#919191'
            }
            borderColor={
              (!matchRatingsEditorEnabled || isMatchRatingsEditorLoaded) && !hasError && getBrandLogoUrl() != null
                ? 'BLACK'
                : '#919191'
            }
            text={languageTexts.brand_text_10}
            onClick={async () => {
              if (matchRatingsEditorEnabled && !isMatchRatingsEditorLoaded) {
                props.showModal({ text: languageTexts.is_requesting });
                return false;
              }
              if (hasError) {
                props.showModal({ text: hasError });
                return false;
              }
              setHasError(languageTexts.is_requesting);
              await submit();
              setHasError(undefined);
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto w-full" style={{ maxWidth: '385px' }}>
      <div className="px-5 mx-auto w-full">
        {renderPageTitle()}
        {renderBrandLogo()}
        {renderForm()}
      </div>
    </div>
  );
};

export default connect(mapStateToProps, mapDispatchProps)(BrandIndex);
